import {useLiveQuery} from "dexie-react-hooks";
import {db} from "@/app/storage";

/**
 * Library statistics interface
 */
export interface LibraryStats {
  totalEntries: number;
  dueForReview: number;
  byType: {
    vocab: number;
    character: number;
  };
  byGrouping: Record<string, number>;
}

/**
 * Hook to fetch library statistics
 *
 * Queries IndexedDB for all active library.entry records and computes
 * aggregate statistics for the dashboard.
 */
export function useLibraryStats() {
  const stats = useLiveQuery(async () => {
    const allEntries = await db.libraryEntries.where("isDeleted").equals(0).toArray();
    const now = new Date().toISOString();

    const computedStats: LibraryStats = {
      totalEntries: allEntries.length,
      dueForReview: 0,
      byType: {
        vocab: 0,
        character: 0,
      },
      byGrouping: {},
    };

    for (const entry of allEntries) {
      // 1. Calculate items due for review
      if (entry.srsState.dueAt <= now) {
        computedStats.dueForReview++;
      }

      // 2. Count by type (vocab, character, and future grammar)
      if (entry.type === "vocab") {
        computedStats.byType.vocab++;
      } else if (entry.type === "character") {
        computedStats.byType.character++;
      }

      // 3. Count by grouping
      if (entry.grouping) {
        computedStats.byGrouping[entry.grouping] =
          (computedStats.byGrouping[entry.grouping] || 0) + 1;
      }
    }

    return computedStats;
  }, []);

  return {
    stats: stats || null,
    isLoading: stats === undefined,
    error: null,
  };
}
